import { useEffect, useState } from 'react'
import { Button, Field, HStack, Input, Text, VStack } from '@chakra-ui/react'
import { toaster } from '@/components/ui/toaster'
import { usePointOfContact, useSetPointOfContact } from '../../../hooks/useCampaign'
import { extractApiMessage } from '../../../lib/apiError'
import { formatPhone, isValidEmail, normalizePhone } from '../../../lib/contactMethods'
import { Card } from '../../shared'

/**
 * [Canvasser redesign Phase 1 / spec D3, §4.11] Who canvassers should contact for this campaign.
 * Shown on their waiting room (and Phase 7 profile). NOT the campaign creator. Saved through its own
 * endpoint so it never clobbers other campaign fields. The client checks are pre-flight only — the
 * server's PhoneNormalizer / [EmailAddress] are the gate.
 */
export function PointOfContactEditor({ campaignId }: { campaignId: string }) {
  const { data, isLoading, error: loadError } = usePointOfContact(campaignId)
  // A failed GET locks the form: saving would overwrite a POC the organizer never saw.
  const locked = isLoading || !!loadError
  const save = useSetPointOfContact(campaignId)
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [formError, setFormError] = useState<string | null>(null)

  useEffect(() => {
    if (!data) return
    setName(data.name ?? '')
    setPhone(data.phone ? formatPhone(data.phone) : '')
    setEmail(data.email ?? '')
  }, [data])

  const onSave = async () => {
    if (locked) return
    const body = {
      name: name.trim() || null,
      phone: phone.trim() || null,
      email: email.trim() || null,
    }
    if (body.phone && !normalizePhone(body.phone)) {
      setFormError('Enter a 10-digit US phone number, or leave it blank.')
      return
    }
    if (body.email && !isValidEmail(body.email)) {
      setFormError('Enter a valid email address, or leave it blank.')
      return
    }
    setFormError(null)
    try {
      await save.mutateAsync(body)
      toaster.create({ type: 'success', title: 'Point of contact saved' })
    } catch (err) {
      setFormError(extractApiMessage(err, 'Failed to save the point of contact.'))
    }
  }

  return (
    <Card>
      <VStack align="stretch" gap={3}>
        <VStack align="start" gap={0}>
          <Text fontWeight="medium">Point of contact for canvassers</Text>
          <Text fontSize="sm" color="muted">
            Canvassers see this while they wait for turf. Leave a field blank to hide it.
          </Text>
        </VStack>
        <Field.Root>
          <Field.Label>Name</Field.Label>
          <Input value={name} onChange={e => setName(e.target.value)} disabled={locked} maxLength={200} minH="44px" />
        </Field.Root>
        <HStack gap={3} align="start" flexWrap="wrap">
          <Field.Root flex="1" minW="12rem">
            <Field.Label>Phone</Field.Label>
            <Input value={phone} onChange={e => setPhone(e.target.value)} disabled={locked} inputMode="tel" minH="44px" />
          </Field.Root>
          <Field.Root flex="1" minW="12rem">
            <Field.Label>Email</Field.Label>
            <Input value={email} onChange={e => setEmail(e.target.value)} disabled={locked} inputMode="email" maxLength={320} minH="44px" />
          </Field.Root>
        </HStack>
        {loadError && (
          <Text fontSize="sm" color="danger.fg">
            {extractApiMessage(loadError, "Couldn't load the point of contact.")}
          </Text>
        )}
        {formError && <Text fontSize="sm" color="danger.fg">{formError}</Text>}
        <HStack justify="end">
          <Button colorPalette="brand" minH="44px" onClick={onSave} loading={save.isPending} disabled={locked}>
            Save contact
          </Button>
        </HStack>
      </VStack>
    </Card>
  )
}
